import React, { memo, useMemo, useEffect, useCallback } from 'react';
import { View, Text } from 'react-native';
import { ActionButtons, DataTable, Navigation } from '../../components';
import { __WEB__, THEME } from '../../../constants/styles';
import momentTz from 'moment-timezone';

import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import { getAppointmentRequests } from './slice';

import { EVENT_TYPE_CMR, EVENT_TYPE_CMR_FLY } from '../../../constants/eventTypes';

function typeLabel(eventType) {
  switch (eventType) {
    case EVENT_TYPE_CMR:
      return 'Non-Flyer';
    case EVENT_TYPE_CMR_FLY:
      return 'Flyer';
    default:
      return eventType;
  }
}

const Actions = memo(function Actions(props) {
  const item = props.item;
  const beingUpdated = !!props.beingUpdated[item.appointmentRequestId || item.email];
  const disabled = !props.canEdit || beingUpdated || item.status !== null;
  return (
    <ActionButtons
      item={item}
      onContactPress={props.handlers?.onContactPress}
      onInfoPress={props.handlers?.onInfoPress}
      onRejectPress={props.handlers?.onRejectPress}
      onAcceptPress={props.handlers?.onAcceptPress}
      rejectDisabled={disabled}
      acceptDisabled={disabled}
      acceptButtonTestId={`approve-${item.email}`}
      rejectButtonTestId={`reject-${item.email}`}
    />
  );
});

const NativeRow = memo(function NativeRow(props) {
  const r = props.r;
  const details = r.details;
  return (
    <View style={THEME.basicRow}>
      <View style={[THEME.rowColumn, { flexGrow: 1 }]}>
        <Text style={THEME.rowText}>{details.full_name}</Text>
        <Text style={THEME.rowText}>
          {r.appointmentDate} - {r.appointmentTime}
        </Text>
      </View>
      <View style={[{ width: 150 }]}>
        <Actions item={r} {...props} handlers={props.handlers} />
      </View>
    </View>
  );
});

function renderNative(props, rowData, handlers) {
  function renderRow(r) {
    return (
      <NativeRow
        r={r}
        key={`${r.email}${r.appointment_type}`}
        beingUpdated={props.beingUpdated}
        canEdit={props.canEdit}
        handlers={handlers}
      />
    );
  }
  return rowData?.map(renderRow);
}

const RequestedAppointments = memo(function RequestedAppointments(props) {
  useEffect(() => {
    if (props.wsConnected && props.employer?.employerId && props.eventType) {
      props.actions.getAppointmentRequests({
        employerId: props.employer?.employerId,
        eventType: props.eventType,
      });
    }
  }, [props.wsConnected, props.employer, props.eventType]);

  const requests = useMemo(() => {
    const activeRequests = props.appointmentRequests?.filter((a) => a.status === null);
    return activeRequests?.sort((a, b) => {
      const dateA = new Date(a.appointment_time);
      const dateB = new Date(b.appointment_time);
      return dateA - dateB;
    });
  }, [props.appointmentRequests]);

  const rowData = useMemo(() => {
    return (
      requests?.map((r) => {
        const apptMoment = momentTz(r.appointment_time).tz(props.employer?.timeZone || 'UTC');
        return {
          ...r,
          item: r,
          key: `${r.email}${r.appointment_type}`,
          patientName: r.details.full_name,
          appointmentDate: apptMoment.format('M/DD/YYYY'),
          appointmentTime: apptMoment.format('HH:mm'),
        };
      }) || []
    );
  }, [requests, props.employer, props.eventType]);

  const onInfoPress = useCallback((r) => {
    Navigation.showModal({
      screen: 'CreatePHARequest',
      title: 'Appointment Info',
      passProps: {
        request: r,
        readOnly: true,
        admin: true,
        hideButtons: true,
      },
    });
  }, []);

  const onAcceptPress = useCallback((r) => {
    Navigation.showModal({
      screen: 'CreatePHARequest',
      title: 'Appointment Info',
      passProps: {
        readOnly: true,
        admin: true,
        request: r,
      },
    });
  }, []);

  const onRejectPress = useCallback((r) => {
    const title = 'Reject this request?';
    Navigation.showModal({
      screen: 'AppointmentRejectReason',
      title,
      passProps: {
        eventType: r.appointment_type,
        request: r,
      },
    });
  }, []);

  const onContactPress = useCallback(
    (r) => {
      Navigation.showModal({
        screen: 'AppointmentRequestSms',
        title: 'Send Text Message',
        passProps: {
          request: r,
          employerId: props.employer?.employerId,
        },
      });
    },
    [props.employer?.employerId]
  );

  const handlers = useMemo(
    () => ({
      onInfoPress,
      onAcceptPress,
      onRejectPress,
      onContactPress,
    }),
    [onInfoPress, onAcceptPress, onRejectPress, onContactPress]
  );

  if (!__WEB__) return renderNative(props, rowData, handlers);

  const columnData = useMemo(() => {
    const cols = [
      {
        headerLabel: 'NAME',
        style: { flexGrow: 0.7, width: 130 },
        dataAccessor: 'patientName',
      },
      {
        headerLabel: 'DATE REQUESTED',
        style: { flexGrow: 0.1, width: 125 },
        dataAccessor: 'appointmentDate',
      },
      {
        headerLabel: 'TIME SLOT REQUESTED',
        style: { flexGrow: 0.1, width: 125 },
        dataAccessor: 'appointmentTime',
      },
    ];
    if (props.eventType === EVENT_TYPE_CMR || props.eventType === EVENT_TYPE_CMR_FLY) {
      cols.push({
        headerLabel: 'TYPE',
        style: { flexGrow: 0.1, width: 110 },
        dataAccessor: (item) => (
          <Text style={THEME.rowText}>{typeLabel(item.appointment_type)}</Text>
        ),
      });
    }
    cols.push({
      headerLabel: 'ACTIONS',
      style: { flexGrow: 0.1, width: 170 },
      extraRowStyle: { paddingLeft: 5 },
      dataAccessor: (item) => <Actions item={item} {...props} handlers={handlers} />,
    });
    return cols;
  }, [handlers, props.eventType]);
  return <DataTable columns={columnData} data={rowData} />;
});

function mapStateToProps(state) {
  return {
    beingUpdated: state.screens.employer.beingUpdated,
    appointmentRequests: state.screens.scheduler.appointmentRequests,
    wsConnected: state.application.wsConnected,
  };
}
function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        getAppointmentRequests,
      },
      dispatch
    ),
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(RequestedAppointments);
